'use strict';

/**
 * Shared plumbing for the event-date inputs (`.js-input-date`, rendered by the
 * `isinputdate` ISML module). The calendar is the accessible ab-datepicker
 * (WAI-ARIA grid, arrow-key navigation) exposed as $.fn.abDatePicker by
 * thirdParty/abDatepicker.js, rendered inline inside the field container so
 * it stays open and keyboard-reachable (FRIAR-1454, AudioEye #47/#48).
 */

const SELECTORS = {
    fieldWrapper: '.js-field-wrapper',
    calendarContainer: '.js-input-date-calendar',
    inputGroup: '.input-group',
    formGroup: '.form-group',
    gridCell: 'td',
    selectableCell: 'td.selectable',
    currentDay: '.curDay',
    offscreenLabels: '.offscreen'
};

const CLASSES = {
    theme: 'friartux',
    hasValue: 'has-value',
    currentDay: 'curDay'
};

const DATA_KEY = 'ab.datepicker';
const DEFAULT_FORMAT = 'MM-dd-y';

/**
 * Zero-pad a number to two digits.
 * @param {number} n - number
 * @returns {string} padded number
 */
const pad = (n) => (n < 10 ? '0' + n : String(n));

/**
 * Format a Date with the subset of CLDR tokens this project uses (MM, dd, y).
 * Used before the plugin exists (its `min` option must be a formatted string).
 * @param {Date} date - date to format
 * @param {string} format - e.g. 'MM-dd-y' or 'y-MM-dd'
 * @returns {string} formatted date
 */
const formatDate = (date, format) => format
    .replace('MM', pad(date.getMonth() + 1))
    .replace('dd', pad(date.getDate()))
    .replace('y', String(date.getFullYear()));

/**
 * Midnight today plus N days.
 * @param {number} days - offset in days
 * @returns {Date} date
 */
const daysFromToday = (days) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + (parseInt(days, 10) || 0));

    return date;
};

/**
 * The ab-datepicker instance attached to an input, if any.
 * @param {jQuery} $input - date input
 * @returns {Object|null} plugin instance
 */
const getInstance = ($input) => ($input && $input.length ? $input.data(DATA_KEY) || null : null);

/**
 * Selected date as a Date object, or null when the field is empty/unparseable.
 * (ab-datepicker's own getDate() returns *today* for an empty field, which would
 * silently submit a date the shopper never chose.)
 * @param {jQuery} $input - date input
 * @returns {Date|null} selected date
 */
const getDate = ($input) => {
    const instance = getInstance($input);
    const value = $input && $input.length ? $.trim($input.val()) : '';

    if (!instance || value === '') {
        return null;
    }

    const date = instance.parseDate(value);

    return date && !isNaN(date.getTime()) ? date : null;
};

/**
 * Whether a date lies inside the calendar's selectable range (`min`/`max` options,
 * which the plugin stores as Date objects).
 * @param {Date} date - date to test
 * @param {Object} instance - plugin instance
 * @returns {boolean} true when selectable
 */
const isInRange = (date, instance) => {
    const min = instance.options.min;
    const max = instance.options.max;

    return (!min || date >= min) && (!max || date <= max);
};

/**
 * Same calendar day, ignoring time of day (the plugin seeds its date with `new Date()`).
 * @param {Date} a - first date
 * @param {Date} b - second date
 * @returns {boolean} true when both fall on the same day
 */
const isSameDay = (a, b) => !!a && !!b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

/**
 * Keep the grid's highlighted day in sync with the input value (typed or picked)
 * and flag the calendar so the selected-day styling only shows once a value exists.
 *
 * The plugin's update() writes the input on a pick but never records it in
 * `dateObj`, and its setDate() rebuilds the whole grid and rewrites the month
 * heading, which is an assertive live region: a screen reader would hear the
 * month again after every pick. So when the date is already in the month on
 * screen only the plugin's bookkeeping and the `curDay` marker are updated, and
 * setDate() is reserved for a typed date in another month.
 *
 * A value outside the selectable range is left alone (no clamping to `min`, no
 * aria-invalid) so form validation reports it instead of the field silently
 * changing under the shopper.
 * @param {jQuery} $input - date input
 * @param {Object} instance - plugin instance
 * @returns {void}
 */
const syncCalendar = ($input, instance) => {
    const date = getDate($input);
    const selectable = !!date && isInRange(date, instance);

    instance.$calendar.toggleClass(CLASSES.hasValue, selectable);

    if (!selectable || isSameDay(date, instance.dateObj)) {
        return;
    }

    if (date.getMonth() === instance.month && date.getFullYear() === instance.year) {
        const $cell = instance.$grid.find("tbody td[data-value='" + date.getDate() + "']");

        instance.dateObj = date;
        instance.curYear = date.getFullYear();
        instance.curMonth = date.getMonth();
        instance.date = date.getDate();
        instance.$grid.find(SELECTORS.currentDay).removeClass(CLASSES.currentDay);
        $cell.addClass(CLASSES.currentDay);
        instance.$grid.attr('aria-activedescendant', $cell.attr('id'));

        return;
    }

    // Another month: let the plugin re-render. That drops keyboard focus to <body>
    // if a day cell was focused, so remember that first and put focus back.
    const gridHasFocus = instance.$grid.is(':focus') || instance.$grid.find(':focus').length > 0;

    instance.setDate(date);

    if (gridHasFocus) {
        instance.selectGridCell(instance.$grid.attr('aria-activedescendant'));
    }
};

/**
 * Initialise the accessible calendar on one input (idempotent).
 * @param {jQuery} $input - date input
 * @param {Object} [options] - settings
 * @param {string} [options.format] - output format, CLDR tokens (default 'MM-dd-y')
 * @param {number} [options.minDays] - first selectable day, as days from today (default 0)
 * @param {Function} [options.onUpdate] - called with the formatted value after a pick
 * @returns {Object} plugin instance
 */
const initInput = ($input, options) => {
    const settings = options || {};
    const existing = getInstance($input);

    if (existing) {
        return existing;
    }

    const format = settings.format || DEFAULT_FORMAT;
    const minDate = daysFromToday(settings.minDays);
    let $container = $input.closest(SELECTORS.fieldWrapper).find(SELECTORS.calendarContainer).first();
    if (!$container.length) {
        $container = $input.parent();
    }

    const inputId = $input.attr('id');
    const placeholder = $input.attr('placeholder');
    const initialValue = $input.val();

    // The plugin derives every internal id (grid cells, month heading, labels) from
    // the input id. The same form can render more than once per page, so let it
    // generate a unique id and restore ours afterwards.
    $input.removeAttr('id');

    // Construct on an empty field: given a value, the plugin would clamp anything
    // before `min` to `min`, overwrite the field with it and flag it invalid. The
    // real value is restored and synced (without clamping) below.
    $input.val('');

    $input.abDatePicker({
        markup: 'bootstrap4',
        theme: CLASSES.theme,
        startView: 'days',
        firstDayOfWeek: 0,
        inputFormat: [format, 'M/d/y', 'MM/dd/y', 'M-d-y'],
        outputFormat: format,
        min: formatDate(minDate, format),
        inline: $container,
        gainFocusOnConstruction: false,
        onUpdate: function (value) {
            if (typeof settings.onUpdate === 'function') {
                settings.onUpdate(value);
            }
        }
    });

    const instance = getInstance($input);

    if (inputId) {
        $input.attr('id', inputId);
        instance.$calendar.attr('aria-controls', inputId);
    }

    // Undo the plugin's structural changes: the floating label relies on
    // `.f-field-input + .f-field-label`, so the input must stay a direct child
    // of the field with the label right after it.
    instance.$button.remove();
    if ($input.parent().is(SELECTORS.inputGroup)) {
        $input.unwrap();
    }
    $input.css('min-width', '');
    if (typeof placeholder === 'undefined') {
        $input.removeAttr('placeholder');
    } else {
        $input.attr('placeholder', placeholder);
    }

    // Reading/tab order should follow the visual order: month title, then the
    // previous/next arrows (the plugin's markup puts "next" before "prev").
    instance.$monthObj.parent().append(instance.$monthObj, instance.$prev, instance.$next);

    // The plugin's offscreen label texts only exist to name the arrows through
    // aria-labelledby (which still resolves from hidden nodes). Left visible to the
    // accessibility tree they are read out as plain text after the grid in
    // screen-reader browse mode, including labels for the open/close/year buttons
    // this calendar does not have.
    instance.$calendar.find(SELECTORS.offscreenLabels).attr('aria-hidden', 'true');

    // The plugin seeds its date with "today" and flags the (empty) field invalid
    // whenever today is before the first selectable date. An untouched field must
    // not start out flagged.
    $input.removeAttr('aria-invalid');
    $input.closest(SELECTORS.formGroup).removeClass('has-error');

    // Keep the plugin's `.focus`/aria-selected state on the cell that really has
    // focus. The plugin only clears it when the <table> itself blurs, which never
    // happens once focus sits on a cell, so a day would keep looking focused after
    // the shopper tabs on to the Confirm button...
    instance.$grid.on('focusout', SELECTORS.gridCell, (e) => {
        if (!e.relatedTarget || !$.contains(instance.$grid[0], e.relatedTarget)) {
            instance.unSelectGridCell(e.currentTarget.id);
        }
    });
    // ...and, conversely, when the <table> is tabbed into it hands focus to the
    // active cell, then its own blur handler strips that cell's state again.
    instance.$grid.on('focusin', SELECTORS.selectableCell, (e) => {
        if (!$(e.currentTarget).hasClass('focus')) {
            instance.selectGridCell(e.currentTarget.id);
        }
    });

    $input.val(initialValue);
    $input.on('change', () => syncCalendar($input, instance));
    syncCalendar($input, instance);

    return instance;
};

module.exports = {
    initInput: initInput,
    getInstance: getInstance,
    getDate: getDate,
    formatDate: formatDate
};
